/**
 * Cognito sign-in (Epic 1, ADR-0006).
 *
 * Talks to the Cognito JSON API directly with `fetch`. No SDK: these operations
 * are unauthenticated and need no request signing, the strict CSP already permits
 * the endpoint (`web-stack.ts`), and the SRP proof itself is in `srp.ts`.
 *
 * The password never crosses the wire -- the user pool client enables only
 * `ALLOW_USER_SRP_AUTH`, so what is sent is a proof of knowledge.
 */
import { passwordClaim, poolNameFrom, startHandshake, type Ephemeral } from './srp.js';

/**
 * Read on use rather than at module scope, so importing this file needs no
 * configuration -- the same reason the Lambda handlers build their store lazily.
 * Vite substitutes these at build time either way.
 */
const config = () => ({
  userPoolId: import.meta.env['VITE_USER_POOL_ID'] ?? '',
  clientId: import.meta.env['VITE_USER_POOL_CLIENT_ID'] ?? '',
});

/** The region is already in the pool id, so it is not a separate build-time value to keep in step. */
const region = (userPoolId: string): string => {
  const prefix = userPoolId.split('_')[0];
  if (prefix === undefined || prefix === '') throw new Error('No user pool configured');
  return prefix;
};

export const isConfigured = (): boolean => {
  const { userPoolId, clientId } = config();
  return userPoolId !== '' && clientId !== '';
};

export interface Tokens {
  readonly accessToken: string;
  readonly idToken: string;
  readonly refreshToken: string;
  /** Epoch milliseconds. The access token lives an hour (auth-stack.ts). */
  readonly expiresAt: number;
}

export type SignInResult =
  | { readonly status: 'signed-in'; readonly tokens: Tokens }
  /** An administratively created account on its first sign-in (bootstrap §7). */
  | {
      readonly status: 'new-password-required';
      readonly session: string;
      readonly username: string;
    }
  /** FR-1.7: TOTP is available, not compulsory. */
  | { readonly status: 'mfa-required'; readonly session: string; readonly username: string };

/**
 * A Cognito failure with a message safe to show someone.
 *
 * NFR-UX-8 wants plain language and no status codes. `code` is kept separately so
 * the UI can branch without parsing prose.
 */
export class AuthError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Deliberately vague about whether an account exists.
 *
 * The user pool sets `PreventUserExistenceErrors` (NFR-SEC-6), so Cognito already
 * returns the same error for an unknown address and a wrong password. These
 * messages must not undo that by being more specific than the service is.
 */
const MESSAGES: Record<string, string> = {
  NotAuthorizedException: 'That email and password do not match an account.',
  UserNotFoundException: 'That email and password do not match an account.',
  UserNotConfirmedException: 'Please confirm your email address first.',
  PasswordResetRequiredException: 'Your password needs resetting before you can sign in.',
  CodeMismatchException: 'That code is not right. Please try again.',
  ExpiredCodeException: 'That code has expired. Please request another.',
  InvalidPasswordException: 'That password does not meet the requirements.',
  LimitExceededException: 'Too many attempts. Please wait a moment and try again.',
  TooManyRequestsException: 'Too many attempts. Please wait a moment and try again.',
};

/**
 * Per-call wording, over the shared vocabulary.
 *
 * `NotAuthorizedException` means "those credentials are wrong" when signing in
 * and "that current password is wrong" when changing one, and a single sentence
 * cannot be true of both. The shared map stays the default; a flow that needs
 * to say something else says it here rather than by inspecting `code` at the
 * call site, which is how two places end up disagreeing about one error.
 */
interface Wording {
  readonly fallback?: string;
  readonly messages?: Record<string, string>;
}

async function cognito<T>(action: string, body: unknown, wording: Wording = {}): Promise<T> {
  if (!isConfigured()) throw new AuthError('not_configured', 'Sign-in is not configured.');

  const { userPoolId } = config();
  let response: Response;
  try {
    response = await fetch(`https://cognito-idp.${region(userPoolId)}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-amz-json-1.1',
        'x-amz-target': `AWSCognitoIdentityProviderService.${action}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    // Offline, or the request never left. Distinguished from a rejection so the
    // UI can say something true rather than implying the credentials were wrong.
    throw new AuthError('network', 'Could not reach the sign-in service.');
  }

  const text = await response.text();
  const payload = text === '' ? {} : (JSON.parse(text) as Record<string, unknown>);

  if (!response.ok) {
    // Cognito reports the type as `__type`, sometimes prefixed with a namespace.
    const raw = typeof payload['__type'] === 'string' ? payload['__type'] : 'UnknownError';
    const code = raw.split('#').pop() ?? raw;
    throw new AuthError(
      code,
      wording.messages?.[code] ??
        MESSAGES[code] ??
        wording.fallback ??
        'Sign-in failed. Please try again.',
    );
  }

  return payload as T;
}

interface AuthResponse {
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
  AuthenticationResult?: {
    AccessToken: string;
    IdToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
  };
}

function tokensFrom(
  result: NonNullable<AuthResponse['AuthenticationResult']>,
  fallbackRefresh = '',
): Tokens {
  return {
    accessToken: result.AccessToken,
    idToken: result.IdToken,
    // Absent when refreshing: the refresh token is issued once and reused.
    refreshToken: result.RefreshToken ?? fallbackRefresh,
    expiresAt: Date.now() + result.ExpiresIn * 1000,
  };
}

function interpret(response: AuthResponse, username: string, refresh = ''): SignInResult {
  if (response.AuthenticationResult) {
    return { status: 'signed-in', tokens: tokensFrom(response.AuthenticationResult, refresh) };
  }
  const session = response.Session ?? '';
  if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    return { status: 'new-password-required', session, username };
  }
  if (response.ChallengeName === 'SOFTWARE_TOKEN_MFA' || response.ChallengeName === 'MFA_SETUP') {
    return { status: 'mfa-required', session, username };
  }
  throw new AuthError(
    'unexpected_challenge',
    'Sign-in needs a step this app does not support yet.',
  );
}

/**
 * Signs in with SRP.
 *
 * Two round trips: the first offers `A` and gets back the salt and the server's
 * `B`; the second returns a proof derived from them and the password.
 */
export async function signIn(email: string, password: string): Promise<SignInResult> {
  const ephemeral: Ephemeral = startHandshake();

  const challenge = await cognito<AuthResponse>('InitiateAuth', {
    AuthFlow: 'USER_SRP_AUTH',
    ClientId: config().clientId,
    AuthParameters: { USERNAME: email, SRP_A: ephemeral.A.toString(16) },
  });

  if (challenge.ChallengeName !== 'PASSWORD_VERIFIER') {
    // Any other first challenge is not something this flow can answer.
    return interpret(challenge, email);
  }

  const parameters = challenge.ChallengeParameters ?? {};
  // Everything downstream keys off this rather than the typed address: Cognito
  // identifies the account internally and the signature must match what it used.
  const userIdForSrp = parameters['USER_ID_FOR_SRP'] ?? email;

  const claim = await passwordClaim({
    poolName: poolNameFrom(config().userPoolId),
    userIdForSrp,
    password,
    saltHex: parameters['SALT'] ?? '',
    srpBHex: parameters['SRP_B'] ?? '',
    secretBlock: parameters['SECRET_BLOCK'] ?? '',
    ephemeral,
  });

  const result = await cognito<AuthResponse>('RespondToAuthChallenge', {
    ChallengeName: 'PASSWORD_VERIFIER',
    ClientId: config().clientId,
    ...(challenge.Session === undefined ? {} : { Session: challenge.Session }),
    ChallengeResponses: {
      USERNAME: userIdForSrp,
      PASSWORD_CLAIM_SECRET_BLOCK: parameters['SECRET_BLOCK'] ?? '',
      PASSWORD_CLAIM_SIGNATURE: claim.signature,
      TIMESTAMP: claim.timestamp,
    },
  });

  return interpret(result, userIdForSrp);
}

/** FR-1.3, and the first sign-in of an administratively created account. */
export async function completeNewPassword(params: {
  username: string;
  session: string;
  newPassword: string;
}): Promise<SignInResult> {
  const result = await cognito<AuthResponse>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: config().clientId,
    Session: params.session,
    ChallengeResponses: { USERNAME: params.username, NEW_PASSWORD: params.newPassword },
  });
  return interpret(result, params.username);
}

/** FR-1.7. */
export async function submitMfaCode(params: {
  username: string;
  session: string;
  code: string;
}): Promise<SignInResult> {
  const result = await cognito<AuthResponse>('RespondToAuthChallenge', {
    ChallengeName: 'SOFTWARE_TOKEN_MFA',
    ClientId: config().clientId,
    Session: params.session,
    ChallengeResponses: { USERNAME: params.username, SOFTWARE_TOKEN_MFA_CODE: params.code },
  });
  return interpret(result, params.username);
}

/** FR-1.2: stay signed in across restarts. The refresh token lasts 30 days. */
/**
 * Starts a password reset (FR-1.4).
 *
 * Returns where Cognito says it sent the code, masked -- or null if it did not
 * say. The caller must not treat a result as proof that an account exists:
 * `PreventUserExistenceErrors` makes this succeed for an unknown address too,
 * and inventing a plausible destination is exactly how it keeps that secret
 * (NFR-SEC-6). The wording around it has to hold either way.
 */
export async function requestPasswordReset(email: string): Promise<string | null> {
  const response = await cognito<{ CodeDeliveryDetails?: { Destination?: string } }>(
    'ForgotPassword',
    { ClientId: config().clientId, Username: email },
    {
      fallback: 'Could not start a password reset. Please try again.',
      messages: {
        // Said without confirming or denying that the address is registered.
        UserNotFoundException: 'If that address has an account, a code is on its way.',
        InvalidParameterException:
          'That account has no verified email address, so a code cannot be sent.',
      },
    },
  );

  return response.CodeDeliveryDetails?.Destination ?? null;
}

/** Finishes a reset with the emailed code (FR-1.4). */
export async function confirmPasswordReset(params: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  await cognito(
    'ConfirmForgotPassword',
    {
      ClientId: config().clientId,
      Username: params.email,
      ConfirmationCode: params.code,
      Password: params.newPassword,
    },
    {
      fallback: 'Could not set that password. Please try again.',
      messages: {
        // Here it means the code, not the credentials -- the shared sentence
        // about an email and password would be actively misleading.
        NotAuthorizedException: 'That code is no longer valid. Please request another.',
      },
    },
  );
}

/**
 * Changes the password of the signed-in account (FR-1.8).
 *
 * Deliberately does not sign other devices out. `GlobalSignOut` is the usual
 * companion to this, and here it would be destructive: a device holding
 * unpushed events in its outbox has them on that device and nowhere else
 * (risk R4), and signing it out clears the local replica. Losing data is a
 * worse answer to "I changed my password" than leaving a session alive on a
 * device the user still holds. Someone who changes a password *because* a
 * device is lost needs those sessions ended, and that is a separate, deliberate
 * action rather than a side effect of this one.
 */
export async function changePassword(params: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const accessToken = await currentAccessToken();
  if (accessToken === null) {
    throw new AuthError('not_signed_in', 'Please sign in again before changing your password.');
  }

  await cognito(
    'ChangePassword',
    {
      AccessToken: accessToken,
      PreviousPassword: params.currentPassword,
      ProposedPassword: params.newPassword,
    },
    {
      fallback: 'Could not change that password. Please try again.',
      messages: {
        // The account is known and signed in, so there is nothing to conceal:
        // the only thing this can mean is that the current password is wrong.
        NotAuthorizedException: 'That current password is not right.',
        LimitExceededException: 'Too many attempts. Please wait a moment and try again.',
      },
    },
  );
}

export async function refreshTokens(refreshToken: string): Promise<Tokens> {
  const result = await cognito<AuthResponse>('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: config().clientId,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  if (!result.AuthenticationResult) {
    throw new AuthError('refresh_failed', 'Please sign in again.');
  }
  return tokensFrom(result.AuthenticationResult, refreshToken);
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

/**
 * Tokens are persisted, which is risk R3 and an accepted residual risk.
 *
 * FR-1.2 requires staying signed in across app restarts, which means the refresh
 * token must survive in the browser where an XSS flaw could reach it. The
 * mitigations are structural rather than storage-based: a CSP with no external
 * script origin at all, no third-party scripts, and a one-hour access token. See
 * architecture §5.
 *
 * `localStorage` rather than IndexedDB deliberately: it is not sync state, it must
 * not be part of the replica the outbox lives in, and losing it only costs a
 * sign-in.
 */
const STORAGE_KEY = 'intentionalist.tokens';

export function storeTokens(tokens: Tokens): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // Private browsing, or storage denied. Staying signed in is a convenience;
    // failing the sign-in over it would not be.
  }
}

export function storedTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<Tokens>;
    if (typeof parsed.refreshToken !== 'string' || parsed.refreshToken === '') return null;
    return parsed as Tokens;
  } catch {
    return null;
  }
}

export function signOut(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing useful to do, and nothing depends on it having worked.
  }
}

/** A valid access token, refreshing when it is close to expiry. */
export async function currentAccessToken(): Promise<string | null> {
  const tokens = storedTokens();
  if (tokens === null) return null;

  // A minute of slack, so a request does not set off with a token that expires
  // while it is in flight.
  if (tokens.expiresAt - Date.now() > 60_000) return tokens.accessToken;

  try {
    const refreshed = await refreshTokens(tokens.refreshToken);
    storeTokens(refreshed);
    return refreshed.accessToken;
  } catch {
    signOut();
    return null;
  }
}
